<script>
  import { createEventDispatcher } from 'svelte';
  import { X, ShieldAlert, XCircle } from 'lucide-svelte';
  import { _ } from '../lib/i18n.js';
  import SignatureStatePanel from './SignatureStatePanel.svelte';
  import {
    SIGNATURE_STATES,
    signatureStateOf,
    normalizeSignatureState,
    isCountersignable
  } from './signatureState.js';

  /** Which act the attestation covers: 'countersign' or 'confirm'. */
  export let action = 'confirm';
  /** Proposal number shown in the header. */
  export let proposalNumber = '';
  /**
   * The stored signature record this attestation covers — the customer document
   * for a counter-signature, the final one for a confirmation. May be null when
   * the dialog was opened by a server refusal rather than by a known state.
   */
  export let info = null;
  /** State the server reported on a refusal, used when `info` is unavailable. */
  export let signatureState = null;
  /** The server's own refusal sentence, when this dialog follows a refusal. */
  export let serverMessage = null;
  /** True while the attested action is in flight. */
  export let loading = false;
  /** Error text from the attested action's own failure. */
  export let error = null;

  const dispatch = createEventDispatcher();

  /**
   * Deliberately starts false and is never pre-ticked, on any path.
   *
   * The operator does this several times a day: a checkbox that arrives ticked
   * is the same as no checkbox at all, and this one is the whole reason the
   * unverified document may proceed.
   */
  let attested = false;
  let attestationNotes = '';

  // A record synthesised from the refusal keeps the panel honest when the
  // dialog was opened without having read the document's own record.
  $: shownInfo = info || (signatureState ? { state: signatureState } : null);
  $: resolvedState = signatureStateOf(shownInfo) ?? normalizeSignatureState(signatureState);

  /**
   * A manual document cannot be counter-signed by anyone — a QTSP signature
   * over a scan would assert that a picture of a signature is a qualified one.
   * The backend refuses it with `requiresAttestation: false`, so this dialog
   * explains it and names the alternative instead of offering a box to tick
   * that could not help.
   */
  $: blocked = action === 'countersign'
    && resolvedState !== null
    && !isCountersignable(resolvedState);

  // The record we are showing says the document is verified, yet an attestation
  // is being asked for: the state moved under us, or the server disagrees.
  $: stateLooksVerified = !blocked && resolvedState === SIGNATURE_STATES.BOUND;

  $: title = blocked
    ? $_('signatureAttestation.notCountersignableTitle')
    : action === 'countersign'
      ? $_('signatureAttestation.countersignTitle')
      : $_('signatureAttestation.confirmTitle');

  $: consequence = action === 'countersign'
    ? $_('signatureAttestation.countersignConsequence')
    : $_('signatureAttestation.confirmConsequence');

  $: submitLabel = action === 'countersign'
    ? (loading ? $_('signatures.countersigning') : $_('signatures.countersign'))
    : (loading ? $_('signatureAttestation.confirming') : $_('proposals.actions.confirm'));

  function handleSubmit() {
    if (blocked || !attested || loading) return;
    console.log(`[SignatureAttestationModal] Attesting an ${resolvedState || 'unknown'} document for the ${action} action`);
    dispatch('attest', {
      attestUnverifiedSignature: true,
      attestationNotes: attestationNotes.trim() || null
    });
  }

  function handleClose() {
    if (loading) return;
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') handleClose();
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<div class="fixed inset-0 z-50 flex items-center justify-center">
  <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" on:click={handleClose}></div>

  <div class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-xl w-full mx-4 scale-in">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between bg-linear-to-r from-orange-50/60 dark:from-orange-900/30 to-transparent rounded-t-2xl">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-linear-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
          <ShieldAlert class="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 class="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">{$_('signatureAttestation.forProposal')} {proposalNumber}</p>
        </div>
      </div>
      {#if !loading}
        <button
          on:click={handleClose}
          class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
        >
          <X size={20} />
        </button>
      {/if}
    </div>

    <!-- Content -->
    <div class="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
      <!-- The state and, specifically, what could not be verified about it. -->
      <SignatureStatePanel info={shownInfo} showTechnicalDetail={true} />

      {#if serverMessage}
        <p class="text-xs text-gray-500 dark:text-gray-400">{serverMessage}</p>
      {/if}

      {#if blocked}
        <!-- Nothing to attest: no statement by anyone makes a scan
             counter-signable. Explain, and name the way forward. -->
        <p class="text-sm text-gray-700 dark:text-gray-200">{$_('signatureAttestation.notCountersignableBody')}</p>
        <div class="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
          <p class="text-xs text-gray-600 dark:text-gray-300">{$_('signatureAttestation.notCountersignableAlternative')}</p>
        </div>
      {:else}
        <p class="text-sm text-gray-700 dark:text-gray-200">{consequence}</p>

        <!-- What the operator is being asked to have actually done. -->
        <div class="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
          <p class="text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">{$_('signatureAttestation.checklistTitle')}</p>
          <ul class="list-disc list-inside text-xs text-gray-600 dark:text-gray-300 space-y-1">
            <li>{$_('signatureAttestation.checklistDocument')}</li>
            <li>{$_('signatureAttestation.checklistSigner')}</li>
            <li>{$_('signatureAttestation.checklistContent')}</li>
          </ul>
        </div>

        <!-- Never pre-ticked. -->
        <label class="flex items-start cursor-pointer">
          <input
            type="checkbox"
            class="mt-0.5 mr-3 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500"
            bind:checked={attested}
            disabled={loading}
          />
          <span class="text-sm text-gray-800 dark:text-gray-100">{$_('signatureAttestation.checkboxLabel')}</span>
        </label>

        <div>
          <label class="label" for="attestation-notes">{$_('signatureAttestation.notesLabel')}</label>
          <textarea
            id="attestation-notes"
            class="input w-full h-20"
            bind:value={attestationNotes}
            maxlength="500"
            placeholder={$_('signatureAttestation.notesPlaceholder')}
            disabled={loading}
          ></textarea>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{$_('signatureAttestation.notesHint')}</p>
        </div>

        {#if stateLooksVerified}
          <p class="text-xs text-gray-500 dark:text-gray-400">{$_('signatureAttestation.stateChangedHint')}</p>
        {/if}
      {/if}

      {#if error}
        <div class="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <div class="flex items-start">
            <XCircle class="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" />
            <p class="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      {/if}
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 rounded-b-2xl">
      <button type="button" on:click={handleClose} class="btn btn-secondary" disabled={loading}>
        {blocked ? $_('common.close') : $_('common.cancel')}
      </button>
      {#if !blocked}
      <button
        type="button"
        on:click={handleSubmit}
        class="btn bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={!attested || loading}
      >
        {#if loading}
          <svg class="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        {/if}
        {submitLabel}
      </button>
      {/if}
    </div>
  </div>
</div>
